import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { PencilIcon, TrashIcon, TrophyIcon } from '@heroicons/react/24/outline';

interface Tournament {
  id: string;
  name: string;
  start_date: string;
  status: string;
}

interface Champion {
  id: string;
  tournament_id: string;
  division: 'male' | 'female';
  player_name: string;
  player_country: string;
  score: string;
  player_image: string | null;
  highlights: string[];
}

interface ChampionFormData {
  tournament_id: string;
  division: 'male' | 'female';
  player_name: string;
  player_country: string;
  score: string;
  player_image: string;
  highlights: string[];
}

interface RegisteredPlayer {
  id: string;
  first_name: string;
  last_name: string;
  country: string;
  total_score: number | null;
  rounds_played: number;
}

function TournamentChampionsManager() {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [champions, setChampions] = useState<Champion[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChampion, setEditingChampion] = useState<Champion | null>(null);
  const [formData, setFormData] = useState<ChampionFormData>({
    tournament_id: '',
    division: 'male',
    player_name: '',
    player_country: '',
    score: '',
    player_image: '',
    highlights: []
  });
  const [highlightInput, setHighlightInput] = useState('');
  const [registeredPlayers, setRegisteredPlayers] = useState<RegisteredPlayer[]>([]);
  const [playerSuggestions, setPlayerSuggestions] = useState<RegisteredPlayer[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const playerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchTournaments();
    fetchChampions();
  }, []);

  useEffect(() => {
    if (formData.tournament_id) {
      fetchRegisteredPlayers(formData.tournament_id);
    } else {
      setRegisteredPlayers([]);
    }
  }, [formData.tournament_id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
          playerInputRef.current && !playerInputRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTournaments = async () => {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, start_date, status')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setTournaments(data || []);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      toast.error('Failed to fetch tournaments');
    }
  };

  const fetchChampions = async () => {
    try {
      const { data, error } = await supabase
        .from('tournament_champions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setChampions(data || []);
    } catch (error) {
      console.error('Error fetching champions:', error);
    }
  };

  const fetchRegisteredPlayers = async (tournamentId: string) => {
    try {
      const { data } = await supabase
        .from('leaderboard_view')
        .select('id, player_name, country, total_score, rounds_played')
        .eq('tournament_id', tournamentId)
        .order('total_score', { ascending: true });

      if (data) {
        const mapped: RegisteredPlayer[] = data.map((p: any) => ({
          id: p.id,
          first_name: p.player_name.split(' ')[0] ?? '',
          last_name: p.player_name.split(' ').slice(1).join(' ') ?? '',
          country: p.country,
          total_score: p.total_score,
          rounds_played: p.rounds_played,
        }));
        setRegisteredPlayers(mapped);
      }
    } catch {}
  };

  const handlePlayerNameChange = (value: string) => {
    setFormData({ ...formData, player_name: value });
    if (value.trim().length === 0) {
      setPlayerSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const lower = value.toLowerCase();
    const matches = registeredPlayers.filter(p =>
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(lower)
    );
    setPlayerSuggestions(matches);
    setShowSuggestions(matches.length > 0);
  };

  const handleSelectPlayer = (player: RegisteredPlayer) => {
    const fullName = `${player.first_name} ${player.last_name}`.trim();
    const scoreStr = player.total_score === null || player.rounds_played === 0
      ? ''
      : player.total_score === 0
        ? 'E'
        : player.total_score > 0
          ? `+${player.total_score}`
          : `${player.total_score}`;
    setFormData({
      ...formData,
      player_name: fullName,
      player_country: player.country,
      score: scoreStr,
    });
    setShowSuggestions(false);
    setPlayerSuggestions([]);
  };

  const handleOpenModal = (champion?: Champion) => {
    if (champion) {
      setEditingChampion(champion);
      setFormData({
        tournament_id: champion.tournament_id,
        division: champion.division,
        player_name: champion.player_name,
        player_country: champion.player_country,
        score: champion.score,
        player_image: champion.player_image || '',
        highlights: champion.highlights || []
      });
    } else {
      setEditingChampion(null);
      setFormData({
        tournament_id: selectedTournament,
        division: 'male',
        player_name: '',
        player_country: '',
        score: '',
        player_image: '',
        highlights: []
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingChampion(null);
    setFormData({
      tournament_id: '',
      division: 'male',
      player_name: '',
      player_country: '',
      score: '',
      player_image: '',
      highlights: []
    });
    setHighlightInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.tournament_id || !formData.player_name || !formData.player_country || !formData.score) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const championData = {
        ...formData,
        highlights: formData.highlights.length > 0 ? formData.highlights : []
      };

      if (editingChampion) {
        const { error } = await supabase
          .from('tournament_champions')
          .update(championData)
          .eq('id', editingChampion.id);

        if (error) throw error;
        toast.success('Champion updated successfully');
      } else {
        const { error } = await supabase
          .from('tournament_champions')
          .insert([championData]);

        if (error) throw error;
        toast.success('Champion added successfully');
      }

      fetchChampions();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving champion:', error);
      toast.error(error.message || 'Failed to save champion');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this champion?')) return;

    try {
      const { error } = await supabase
        .from('tournament_champions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Champion deleted successfully');
      fetchChampions();
    } catch (error) {
      console.error('Error deleting champion:', error);
      toast.error('Failed to delete champion');
    }
  };

  const addHighlight = () => {
    if (highlightInput.trim()) {
      setFormData({
        ...formData,
        highlights: [...formData.highlights, highlightInput.trim()]
      });
      setHighlightInput('');
    }
  };

  const removeHighlight = (index: number) => {
    setFormData({
      ...formData,
      highlights: formData.highlights.filter((_, i) => i !== index)
    });
  };

  const filteredChampions = selectedTournament
    ? champions.filter(c => c.tournament_id === selectedTournament)
    : champions;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Tournament Champions</h2>
        <button
          onClick={() => handleOpenModal()}
          className="bg-golf-green text-white px-4 py-2 rounded-lg hover:bg-golf-green/90 transition-colors"
        >
          Add Champion
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Tournament
        </label>
        <select
          value={selectedTournament}
          onChange={(e) => setSelectedTournament(e.target.value)}
          className="w-full rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
        >
          <option value="">All Tournaments</option>
          {tournaments.map(tournament => (
            <option key={tournament.id} value={tournament.id}>
              {tournament.name} ({tournament.status})
            </option>
          ))}
        </select>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredChampions.map(champion => {
          const tournament = tournaments.find(t => t.id === champion.tournament_id);
          return (
            <div key={champion.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="relative h-48">
                <img
                  src={champion.player_image || 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=400'}
                  alt={champion.player_name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 bg-golf-orange text-white px-3 py-1 rounded-full text-sm font-medium">
                  {champion.division}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{champion.player_name}</h3>
                    <p className="text-sm text-gray-600">{champion.player_country}</p>
                  </div>
                  <div className="text-golf-orange font-bold text-xl">{champion.score}</div>
                </div>
                <p className="text-sm text-gray-500 mb-3">{tournament?.name}</p>
                {champion.highlights && champion.highlights.length > 0 && (
                  <ul className="space-y-1 mb-4">
                    {champion.highlights.slice(0, 2).map((highlight, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start">
                        <TrophyIcon className="w-4 h-4 text-golf-orange mr-2 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenModal(champion)}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(champion.id)}
                    className="flex-1 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredChampions.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg">
          <TrophyIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No champions found</p>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold mb-6">
                {editingChampion ? 'Edit Champion' : 'Add Champion'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tournament *
                  </label>
                  <select
                    value={formData.tournament_id}
                    onChange={(e) => setFormData({ ...formData, tournament_id: e.target.value })}
                    className="w-full rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
                    required
                  >
                    <option value="">Select a tournament</option>
                    {tournaments.map(tournament => (
                      <option key={tournament.id} value={tournament.id}>
                        {tournament.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Division *
                  </label>
                  <select
                    value={formData.division}
                    onChange={(e) => setFormData({ ...formData, division: e.target.value as 'male' | 'female' })}
                    className="w-full rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
                    required
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Player Name *
                  </label>
                  <input
                    ref={playerInputRef}
                    type="text"
                    value={formData.player_name}
                    onChange={(e) => handlePlayerNameChange(e.target.value)}
                    onFocus={() => {
                      if (playerSuggestions.length > 0) setShowSuggestions(true);
                    }}
                    className="w-full rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
                    placeholder={formData.tournament_id ? 'Start typing to search registered players...' : 'Select a tournament first'}
                    required
                    autoComplete="off"
                  />
                  {showSuggestions && playerSuggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    >
                      {playerSuggestions.map(player => (
                        <button
                          key={player.id}
                          type="button"
                          onMouseDown={() => handleSelectPlayer(player)}
                          className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {player.first_name} {player.last_name}
                            </p>
                            <p className="text-xs text-gray-400">{player.country}</p>
                          </div>
                          {player.rounds_played > 0 && player.total_score !== null && (
                            <span className={`text-sm font-bold ${player.total_score < 0 ? 'text-green-600' : player.total_score > 0 ? 'text-red-500' : 'text-gray-600'}`}>
                              {player.total_score === 0 ? 'E' : player.total_score > 0 ? `+${player.total_score}` : player.total_score}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    value={formData.player_country}
                    onChange={(e) => setFormData({ ...formData, player_country: e.target.value })}
                    className="w-full rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Score *
                  </label>
                  <input
                    type="text"
                    value={formData.score}
                    onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                    placeholder="e.g., -1, +2, E"
                    className="w-full rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Player Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.player_image}
                    onChange={(e) => setFormData({ ...formData, player_image: e.target.value })}
                    placeholder="https://..."
                    className="w-full rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Highlights
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={highlightInput}
                      onChange={(e) => setHighlightInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                      placeholder="Add a highlight"
                      className="flex-1 rounded-lg border-gray-300 focus:border-golf-green focus:ring-golf-green"
                    />
                    <button
                      type="button"
                      onClick={addHighlight}
                      className="bg-golf-green text-white px-4 py-2 rounded-lg hover:bg-golf-green/90"
                    >
                      Add
                    </button>
                  </div>
                  {formData.highlights.length > 0 && (
                    <ul className="space-y-2">
                      {formData.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm">{highlight}</span>
                          <button
                            type="button"
                            onClick={() => removeHighlight(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-golf-green text-white px-4 py-2 rounded-lg hover:bg-golf-green/90 transition-colors"
                  >
                    {editingChampion ? 'Update' : 'Add'} Champion
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentChampionsManager;
